import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../VersionHistoryDrawer/VersionHistoryDrawer.css";
import {
  getSectionLaborEffortCorrectionsHistory,
  getModuleLaborEffortCorrectionsHistory,
  getProjectIncomeCorrectionHistory,
} from "../../../../../api/projectEstimatesApi";
import {
  countFilledCorrectionCells,
  formatIncomeCorrectionAmount,
  sumAllCorrectionImpact,
  correctionImpactMoney,
  incomeCorrectionListTotals,
  pairIncomeCorrectionsToLayers,
  ESTIMATE_CORRECTION_MODULES,
  MODULE_CORRECTION_LABELS,
} from "../correctionStorage";

const formatDateTime = (dateString) => {
  if (!dateString) return "—";
  try {
    return new Date(dateString).toLocaleString("ru-RU");
  } catch (err) {
    return dateString;
  }
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${Math.round(n).toLocaleString("ru-RU")} ₸`;
};

const formatNdsFlag = (value) => {
  if (value == null) return "—";
  return value ? "с НДС" : "без НДС";
};

const formatEffort = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return String(n);
};

const ACTION_LABELS = {
  create: "Создана",
  created: "Создана",
  insert: "Создана",
  add: "Создана",
  post: "Создана",
  update: "Изменена",
  updated: "Изменена",
  patch: "Изменена",
  edit: "Изменена",
  delete: "Удалена",
  deleted: "Удалена",
  remove: "Удалена",
};

const ACTION_COLORS = {
  create: "#2e7d32",
  created: "#2e7d32",
  insert: "#2e7d32",
  add: "#2e7d32",
  post: "#2e7d32",
  update: "#1976d2",
  updated: "#1976d2",
  patch: "#1976d2",
  edit: "#1976d2",
  delete: "#c62828",
  deleted: "#c62828",
  remove: "#c62828",
};

const getActionLabel = (action) => {
  const key = String(action || "").trim().toLowerCase();
  return ACTION_LABELS[key] || action || "—";
};

const getActionColor = (action) => {
  const key = String(action || "").trim().toLowerCase();
  return ACTION_COLORS[key] || "#6c757d";
};

const CorrectionHistoryDrawer = ({
  open,
  onClose,
  projectId,
  sectionIds = [],
  sectionsLookup = {},
  corrections = [],
  incomeCorrections = [],
  incomeCorrectionTotal = null,
  onRestoreCorrection,
  onDeleteCorrection,
  onRestoreBase,
  restoring = false,
  readOnly = false,
}) => {
  const [history, setHistory] = useState([]);
  const [incomeHistory, setIncomeHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const sectionIdsKey = useMemo(() => sectionIds.join(","), [sectionIds]);

  const loadHistory = useCallback(async () => {
    if (!projectId) {
      setHistory([]);
      setIncomeHistory([]);
      return;
    }

    const ids = sectionIdsKey
      ? sectionIdsKey
          .split(",")
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id))
      : [];

    setLoading(true);
    setError(null);
    try {
      const [lists, moduleLists, incomeItems] = await Promise.all([
        ids.length
          ? Promise.all(
              ids.map((sectionId) =>
                getSectionLaborEffortCorrectionsHistory(
                  projectId,
                  sectionId,
                ).catch(() => []),
              ),
            )
          : Promise.resolve([]),
        Promise.all(
          ESTIMATE_CORRECTION_MODULES.map((module) =>
            getModuleLaborEffortCorrectionsHistory(projectId, module).catch(
              () => [],
            ),
          ),
        ),
        getProjectIncomeCorrectionHistory(projectId).catch(() => []),
      ]);
      const merged = [...lists.flat(), ...moduleLists.flat()]
        .filter(Boolean)
        .sort((a, b) => {
          const timeA = new Date(a.changed_at || 0).getTime();
          const timeB = new Date(b.changed_at || 0).getTime();
          return timeB - timeA;
        });
      setHistory(merged);
      setIncomeHistory(
        [...(Array.isArray(incomeItems) ? incomeItems : [])]
          .filter(Boolean)
          .sort((a, b) => {
            const timeA = new Date(a.changed_at || 0).getTime();
            const timeB = new Date(b.changed_at || 0).getTime();
            return timeB - timeA;
          }),
      );
    } catch (err) {
      setError(err?.message || "Не удалось загрузить историю корректировок");
      setHistory([]);
      setIncomeHistory([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, sectionIdsKey]);

  const correctionIdsKey = useMemo(
    () => corrections.map((item) => item?.id).join(","),
    [corrections],
  );

  useEffect(() => {
    if (open) {
      loadHistory();
    }
  }, [open, loadHistory, correctionIdsKey]);

  const currentId = corrections[corrections.length - 1]?.id;
  const incomeByLayer = useMemo(
    () => pairIncomeCorrectionsToLayers(incomeCorrections, corrections),
    [incomeCorrections, corrections],
  );
  const estimateCorrectionMoney = correctionImpactMoney(
    sumAllCorrectionImpact(corrections),
  );
  const billedIncome = incomeCorrectionListTotals(
    incomeCorrections,
    0,
    incomeCorrectionTotal,
  ).income;
  const correctionDiff = billedIncome - estimateCorrectionMoney;

  const resolveSectionName = (sectionId) =>
    sectionsLookup?.[sectionId]?.name || `Раздел ${sectionId}`;

  const resolveBlockName = (sectionId, teiId) =>
    sectionsLookup?.[sectionId]?.blocks?.[teiId] ||
    (teiId != null ? `ТЭИ ${teiId}` : "Всего");

  const resolveHistoryPlace = (item) => {
    if (item?.module) {
      return MODULE_CORRECTION_LABELS[item.module] || item.module;
    }
    return resolveSectionName(item.section_id);
  };

  const resolveHistoryDetail = (item) => {
    if (item?.module) {
      return item.role || "—";
    }
    return resolveBlockName(item.section_id, item.tei_id);
  };

  const formatComment = (item) => {
    const raw = item?.new_comment || item?.old_comment || "";
    if (!raw.trim() || /^layer:\d+$/i.test(raw.trim())) return "—";
    return raw;
  };

  if (!open) return null;

  return (
    <>
      <div className="version-drawer-backdrop" onClick={onClose} />
      <div className="version-drawer">
        <div className="version-drawer-header">
          <span className="version-drawer-title">История корректировок</span>
          <button className="version-drawer-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="version-drawer-content">
          <p className="correction-drawer-hint">
            Журнал изменений с сервера. Откат снимает поздние слои вместе с
            их суммами заказчику, базовые трудозатраты сметы не меняются.
            Удаление снимает выбранный слой полностью. Откат к базе удаляет
            все корректировки.
          </p>

          <div className="correction-drawer-section-title">
            Корректировка дохода
          </div>
          <p className="correction-drawer-hint">
            Каждая корректировка содержит трудозатраты по разделам и сумму
            заказчику. Чтобы выставить новую сумму, нужно добавить новую
            корректировку.
          </p>
          <table className="version-drawer-table">
            <thead>
              <tr>
                <th>Выставлено заказчику</th>
                <th>Корректировки по смете</th>
                <th>Разница</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  {formatMoney(billedIncome)}
                  {corrections.length > 0 ? (
                    <div style={{ fontSize: "12px", color: "#6c757d", marginTop: 4 }}>
                      {corrections.map((layer) => (
                        <div key={layer.id}>
                          Корректировка {layer.number}:{" "}
                          {formatIncomeCorrectionAmount(
                            incomeByLayer.get(layer.number),
                          )}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </td>
                <td>{formatMoney(estimateCorrectionMoney)}</td>
                <td
                  style={{
                    color:
                      correctionDiff > 0
                        ? "#2e7d32"
                        : correctionDiff < 0
                          ? "#c62828"
                          : undefined,
                    fontWeight: 700,
                  }}
                >
                  {formatMoney(correctionDiff)}
                </td>
              </tr>
            </tbody>
          </table>

          <div className="correction-drawer-section-title">Откат слоёв</div>
          {corrections.length === 0 ? (
            <div className="version-drawer-empty" style={{ height: 80 }}>
              <div>Активных слоёв пока нет</div>
            </div>
          ) : (
            <table className="version-drawer-table">
              <thead>
                <tr>
                  <th>Слой</th>
                  <th>Дата</th>
                  <th>Заполненных ячеек</th>
                  <th>Сумма заказчику</th>
                  <th>Статус</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Базовая смета</td>
                  <td>—</td>
                  <td>—</td>
                  <td>—</td>
                  <td>
                    <span className="version-drawer-status">
                      Исходные данные
                    </span>
                  </td>
                  <td>
                    <button
                      className="version-restore-btn"
                      onClick={onRestoreBase}
                      disabled={restoring || readOnly || !onRestoreBase}
                    >
                      Откатить к базе
                    </button>
                  </td>
                </tr>
                {corrections.map((correction) => {
                  const isCurrent = correction.id === currentId;
                  return (
                    <tr key={correction.id}>
                      <td>Корректировка {correction.number}</td>
                      <td>
                        <span className="version-drawer-date">
                          {formatDateTime(correction.createdAt)}
                        </span>
                      </td>
                      <td>{countFilledCorrectionCells(correction)}</td>
                      <td>
                        {formatIncomeCorrectionAmount(
                          incomeByLayer.get(correction.number),
                        )}
                      </td>
                      <td>
                        <span
                          className="version-drawer-status"
                          style={{ color: isCurrent ? "#1976d2" : "#6c757d" }}
                        >
                          {isCurrent ? "Текущая" : "Зафиксирована"}
                        </span>
                      </td>
                      <td>
                        <div className="version-drawer-actions">
                          <button
                            className="version-restore-btn"
                            onClick={() => onRestoreCorrection(correction.id)}
                            disabled={
                              isCurrent ||
                              restoring ||
                              readOnly ||
                              !onRestoreCorrection
                            }
                            title={
                              isCurrent
                                ? "Это текущий слой"
                                : readOnly
                                  ? "Корректировки на согласовании нельзя откатывать"
                                  : "Откатить к этой корректировке"
                            }
                          >
                            Восстановить
                          </button>
                          <button
                            className="version-delete-btn"
                            onClick={() => onDeleteCorrection(correction.id)}
                            disabled={restoring || !onDeleteCorrection}
                            title={
                              !onDeleteCorrection
                                ? "Пока корректировки на согласовании, слой нельзя удалить"
                                : "Удалить этот слой полностью"
                            }
                          >
                            Удалить
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <div className="correction-drawer-section-title">
            Журнал корректировки дохода
          </div>
          {loading ? (
            <div className="version-drawer-loading">
              <div>Загрузка журнала...</div>
            </div>
          ) : incomeHistory.length === 0 ? (
            <div className="version-drawer-empty" style={{ height: 80 }}>
              <div>Записей по доходу пока нет</div>
            </div>
          ) : (
            <table className="version-drawer-table">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Действие</th>
                  <th>Сумма</th>
                  <th>НДС</th>
                  <th>Комментарий</th>
                  <th>Версия</th>
                </tr>
              </thead>
              <tbody>
                {incomeHistory.map((item, index) => (
                  <tr
                    key={
                      item.id ||
                      `${item.correction_id}-${item.changed_at}-${index}`
                    }
                  >
                    <td>
                      <span className="version-drawer-date">
                        {formatDateTime(item.changed_at)}
                      </span>
                    </td>
                    <td>
                      <span
                        className="version-drawer-status"
                        style={{ color: getActionColor(item.action) }}
                      >
                        {getActionLabel(item.action)}
                      </span>
                    </td>
                    <td>
                      {formatMoney(item.old_amount)} →{" "}
                      {formatMoney(item.new_amount)}
                    </td>
                    <td>
                      {formatNdsFlag(item.old_nds_included_in_amount)} →{" "}
                      {formatNdsFlag(item.new_nds_included_in_amount)}
                    </td>
                    <td className="version-drawer-comment">
                      {item.new_comment || item.old_comment || "—"}
                    </td>
                    <td>
                      {item.version !== null && item.version !== undefined
                        ? `v${item.version}`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="correction-drawer-section-title">
            Журнал трудозатрат
          </div>
          {loading ? (
            <div className="version-drawer-loading">
              <div>Загрузка журнала...</div>
            </div>
          ) : error ? (
            <div className="version-drawer-error">
              <div>Ошибка загрузки: {error}</div>
              <button onClick={loadHistory} className="retry-btn">
                Попробовать снова
              </button>
            </div>
          ) : history.length === 0 ? (
            <div className="version-drawer-empty" style={{ height: 120 }}>
              <div>Записей в журнале пока нет</div>
            </div>
          ) : (
            <table className="version-drawer-table">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Действие</th>
                  <th>Раздел / модуль</th>
                  <th>Блок / роль</th>
                  <th>Было → стало</th>
                  <th>Комментарий</th>
                  <th>Версия</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item, index) => (
                  <tr key={item.id || `${item.correction_id}-${item.changed_at}-${index}`}>
                    <td>
                      <span className="version-drawer-date">
                        {formatDateTime(item.changed_at)}
                      </span>
                    </td>
                    <td>
                      <span
                        className="version-drawer-status"
                        style={{ color: getActionColor(item.action) }}
                      >
                        {getActionLabel(item.action)}
                      </span>
                    </td>
                    <td>{resolveHistoryPlace(item)}</td>
                    <td>{resolveHistoryDetail(item)}</td>
                    <td>
                      {formatEffort(item.old_day_efforts)} →{" "}
                      {formatEffort(item.new_day_efforts)}
                    </td>
                    <td className="version-drawer-comment">
                      {formatComment(item)}
                    </td>
                    <td>
                      {item.version !== null && item.version !== undefined
                        ? `v${item.version}`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

export default CorrectionHistoryDrawer;
